import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { KeyValue } from '@angular/common';
import {
  ColumnMode,
  DatatableComponent as NgxDatatableComponent,
  SelectionType,
  TableColumn,
} from '@swimlane/ngx-datatable';

export interface StringMap {
  [prop: string]: string;
}

export const enum DatatableColumnType {
  TIME = 'TIME',
  IMAGE = 'IMAGE',
  ARRAY = 'ARRAY',
}

export interface DatatableColumn extends TableColumn {
  type?: DatatableColumnType;
}

export const enum SearchControlType {
  SELECT = 'SELECT',
}

export interface SearchControl {
  title: string;
  type: SearchControlType;
  property: string;
  bindToColumn?: string;
  options?: StringMap;
  defaultValue?: string | undefined;
}

@Component({
  selector: 'app-datatable',
  styleUrls: ['./datatable.component.scss'],
  templateUrl: './datatable.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class DatatableComponent<T> {
  rows: T[] = [];
  tempRows: T[] = [];
  searchExpression: string | undefined;
  filters: StringMap = {};

  readonly keyvalueKeepOrder = (
    a: KeyValue<string, any>,
    b: KeyValue<string, any>,
  ): number => 0;

  @Input() columns!: DatatableColumn[];
  @Input() searchable: boolean = true;
  @Input() searchFunction!: (
    rowDataObject: T,
    searchExpression: string,
  ) => boolean;
  @Input() columnMode: ColumnMode = ColumnMode.force;
  @Input() minHeight: string = '700px';
  @Input() headerHeight: number = 50;
  @Input() selectionType!: SelectionType;
  @Input() searchControlSettings: SearchControl[] = [];
  @Input() aviodedFilterValues: string[] = [];
  @Input() isSingleSelectView: boolean = false;
  @Input() isTopSection: boolean = false;
  @Input() isSelectTopSection: boolean = false;
  @Input() isCustomFooter: boolean = false;
  @Input() useSelectHeader: boolean = false;

  // output
  @Output() selectionChange: EventEmitter<T[]> = new EventEmitter<T[]>();
  @Output() filterChange = new EventEmitter<StringMap>();

  @ViewChild(NgxDatatableComponent) table?: NgxDatatableComponent;

  @Input('rows') set setRows(rows: T[]) {
    this.rows = rows;
    this.tempRows = [...rows];
    this.filterRows();
  }

  @Input() set initialFilters(initialFilters: StringMap | null | undefined) {
    if (!!initialFilters) {
      this.filters = initialFilters;
      this.filterRows();
    }
  }

  onFilterChange(event: any): void {
    if (
      event.value === '' &&
      this.searchControlSettings.filter(
        (settting) =>
          settting.property === event.id && settting.defaultValue != null,
      ).length === 0
    ) {
      delete this.filters[event.id];
    } else {
      this.filters[event.id] = event.value;
    }
    this.filterRows();
    this.filterChange.emit(this.filters);
  }

  search(event: any): void {
    this.filterRows();
  }

  filterRows(): void {
    if (this.searchExpression) {
      const temp: any[] = [];
      this.tempRows.forEach((rowValue) => {
        let isRowMatch: boolean = false;
        if (this.searchFunction) {
          isRowMatch = this.searchFunction.call(
            this,
            rowValue,
            this.searchExpression!,
          );
        } else if (Object.keys(this.filters).length > 0) {
          isRowMatch = this.isFilterMatch(rowValue);
        }

        isRowMatch =
          (isRowMatch || Object.keys(this.filters).length === 0) &&
          this.isRowMatch(rowValue, this.searchExpression!);

        if (isRowMatch) {
          temp.push(rowValue);
        }
      });
      this.rows = temp;
    } else if (Object.keys(this.filters).length > 0) {
      const temp: any[] = [];
      this.tempRows.forEach((rowValue) => {
        if (this.isFilterMatch(rowValue)) {
          temp.push(rowValue);
        }
      });
      this.rows = temp;
    } else {
      this.rows = this.tempRows;
    }

    if (!!this.table) {
      this.table!.offset = 0;
    }
  }

  // default search function
  private isRowMatch(rowValue: any, searchExpression: string): boolean {
    // case insensitive search
    const se = searchExpression.toLowerCase();

    let match: boolean = false;
    this.columns!.some((col: DatatableColumn) => {
      const key = col.prop ? col.prop : col.name;
      if (key === 'picture' || key === 'room' || key === 'time') {
        return false;
      }
      const cellValue = String(rowValue[key!]);
      if (cellValue.toLowerCase().indexOf(se) !== -1) {
        match = true;
        return true;
      }
      return false;
    });
    return match;
  }

  private isFilterMatch(rowValue: any): boolean {
    for (const key in this.filters) {
      if (Object.prototype.hasOwnProperty.call(this.filters, key)) {
        const value = String(this.filters[key]);
        const cellValue = String(rowValue[key]);
        if (
          !!value &&
          cellValue.toLowerCase() !== value.toLowerCase() &&
          !this.aviodedFilterValues.includes(value)
        ) {
          return false;
        }
      }
    }
    return true;
  }

  getControlForColumn(prop: any) {
    const propAsString = prop.toString();
    const control = this.searchControlSettings.find(
      (control) => control.bindToColumn === propAsString,
    );
    return control;
  }
}
